import { AdminMatches } from '@platform/client';
import { MatchGroupData } from '@platform/client/schemas';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useContext, useMemo } from 'react';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { MatchGroupLink } from '../matches/MatchesAdmin';

const matchColumnHelper = createColumnHelper<MatchGroupData>();

export const MatchesTabContent = ({ professionalId }: { professionalId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: matchData,
    isLoading,
    isError,
    isSuccess,
  } = AdminMatches.useGetProfessionalAdminMatches(professionalId, {
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        matchColumnHelper.accessor('id', {
          header: 'Id',
          cell: (info) => (
            <MatchGroupLink
              matchGroupId={info.getValue()}
              matchGroupName={info.getValue().toString()}
            />
          ),
          size: 5,
        }),
        matchColumnHelper.accessor('initiation_type', {
          id: 'initiation_types',
          header: 'Sent by',
          cell: (info) => info.getValue(),
          size: 10,
        }),
        matchColumnHelper.accessor('created_at', {
          header: 'Created',
          cell: (info) => formatDateStr(info.getValue()),
          size: 1,
        }),
        matchColumnHelper.accessor('organization', {
          id: 'organization_name',
          header: 'Organization',
          cell: (organization) => (
            <OrganizationLink
              organizationId={organization.getValue().id}
              organizationName={organization.getValue().name}
            />
          ),
        }),
        matchColumnHelper.accessor('project', {
          id: 'project_name',
          header: 'Project',
          cell: (project) => (
            <ProjectLink
              projectId={project.getValue()?.id}
              projectName={project.getValue()?.name}
            />
          ),
          size: 300,
        }),
        matchColumnHelper.accessor('progress', {
          id: 'progresses',
          header: 'Progress',
          cell: (info) => info.getValue(),
        }),
        matchColumnHelper.accessor('updated_at', {
          header: 'Updated',
          cell: (info) => formatDateStr(info.getValue()),
          size: 10,
        }),
      ] as ColumnDef<MatchGroupData>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && matchData?.matches.length === 0 ?
        <NoRecordsFoundTabContent recordType='professional' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={matchData?.matches || []}
          isSimple
          filteredCount={matchData?.filtered_count ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
